import React, { useState, useEffect } from "react";
import { API_URL } from "./config";
import { useNavigate } from "react-router-dom";
import './TripCards.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faChartLine, faUsers, faCar, faStar, faCheckCircle, faTimesCircle, faClock, faRoute } from '@fortawesome/free-solid-svg-icons';
import DashboardLayout from './DashboardLayout/DasboardLayout';

const Funnel = () => {
  const APILINK = API_URL;
  const [funnelData, setFunnelData] = useState({ overview: [], detailed: [], metrics: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [selectedRideType, setSelectedRideType] = useState('all'); // New filter state
  const navigate = useNavigate();

  const getFunnelData = async () => {
    try {
      setError(null);
      console.log("Fetching funnel data from:", `${APILINK}/trip/funnel-analysis`);
      const response = await fetch(`${APILINK}/trip/funnel-analysis?period=${selectedPeriod}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log("Funnel data received:", data);
      setFunnelData(data);
    } catch (error) {
      console.error("Error fetching funnel data:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getFunnelData();
    const interval = setInterval(() => {
      getFunnelData();
    }, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [selectedPeriod]);

  const calculateConversionRate = (current, previous) => {
    if (previous === 0) return 0;
    return ((current / previous) * 100).toFixed(1);
  };

  const getFunnelIcon = (stage) => {
    switch(stage) {
      case 'private_search': return faRoute;
      case 'private_new_order': return faCar;
      case 'private_counter_offer': return faChartLine;
      case 'private_matched': return faUsers;
      case 'private_in_progress': return faClock;
      case 'private_trip_ended': return faCheckCircle;
      case 'private_completed': return faCheckCircle;
      case 'private_cancelled': return faTimesCircle;
      case 'private_waiting_payment': return faChartLine;
      case 'private_feedback': return faStar;
      case 'rideshare_driver_created': return faCar;
      case 'rideshare_driver_in_transit': return faClock;
      case 'rideshare_driver_cancelled': return faTimesCircle;
      case 'rideshare_driver_completed': return faCheckCircle;
      case 'rideshare_passenger_request': return faUsers;
      case 'rideshare_passenger_negotiating': return faChartLine;
      case 'rideshare_passenger_accepted': return faCheckCircle;
      case 'rideshare_passenger_in_transit': return faClock;
      case 'rideshare_passenger_declined': return faTimesCircle;
      case 'rideshare_passenger_rejected': return faTimesCircle;
      case 'rideshare_passenger_expired': return faTimesCircle;
      case 'rideshare_passenger_cancelled': return faTimesCircle;
      case 'rideshare_passenger_completed': return faCheckCircle;
      case 'rideshare_passenger_trip_completed': return faCheckCircle;
      default: return faChartLine;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return '#28a745';
      case 'in_progress': return '#007bff';
      case 'cancelled': return '#dc3545';
      case 'pending': return '#ffc107';
      default: return '#6c757d';
    }
  };

  return (
    <DashboardLayout>
      <div className="hearder-feed">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <button
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#FFD700',
              fontSize: '24px',
              marginRight: '10px'
            }}
            onClick={() => navigate('/')}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <h1 style={{ margin: '0 auto', textAlign: 'center', flexGrow: 1 }}>Ride Funnel Analysis</h1>
        </div>

        {/* Period Selector */}
        <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
          {['today', 'week', 'month'].map(period => (
            <button
              key={period}
              onClick={() => setSelectedPeriod(period)}
              style={{
                padding: '8px 16px',
                border: selectedPeriod === period ? '2px solid #007bff' : '1px solid #dee2e6',
                borderRadius: '5px',
                backgroundColor: selectedPeriod === period ? '#007bff' : '#fff',
                color: selectedPeriod === period ? '#fff' : '#007bff',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {period.charAt(0).toUpperCase() + period.slice(1)}
            </button>
          ))}
        </div>

        {/* Ride Type Filter */}
        <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
          {[
            { value: 'all', label: 'All Rides', color: '#6c757d' },
            { value: 'private', label: 'Private Rides', color: '#28a745' },
            { value: 'rideshare_driver', label: 'Rideshare Drivers', color: '#007bff' },
            { value: 'rideshare_passenger', label: 'Rideshare Passengers', color: '#ffc107' }
          ].map(rideType => (
            <button
              key={rideType.value}
              onClick={() => setSelectedRideType(rideType.value)}
              style={{
                padding: '8px 16px',
                border: selectedRideType === rideType.value ? '2px solid ' + rideType.color : '1px solid #dee2e6',
                borderRadius: '5px',
                backgroundColor: selectedRideType === rideType.value ? rideType.color : '#fff',
                color: selectedRideType === rideType.value ? '#fff' : rideType.color,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: selectedRideType === rideType.value ? 'bold' : 'normal'
              }}
            >
              {rideType.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
            <div style={{ fontSize: '18px', marginBottom: '10px' }}>Loading funnel data...</div>
            <div style={{ fontSize: '14px', opacity: 0.7 }}>Please wait while we fetch the latest analytics</div>
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ color: '#e74c3c', fontSize: '18px', marginBottom: '10px' }}>Error loading funnel data</div>
            <div style={{ color: '#7f8c8d', fontSize: '14px', marginBottom: '20px' }}>{error}</div>
            <button 
              onClick={getFunnelData}
              style={{
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="funnel-container">
            {/* Funnel Overview Cards */}
            <div style={{ marginBottom: '30px' }}>
              <h2 style={{ color: '#2c3e50', marginBottom: '20px' }}>
                Ride Funnel Analysis 
                {selectedRideType !== 'all' && (
                  <span style={{ fontSize: '16px', color: '#6c757d', fontWeight: 'normal' }}>
                    - {selectedRideType === 'private' ? 'Private Rides' : 
                       selectedRideType === 'rideshare_driver' ? 'Rideshare Drivers' : 
                       'Rideshare Passengers'}
                  </span>
                )}
              </h2>
              
              {/* Private Rides Section */}
              {(selectedRideType === 'all' || selectedRideType === 'private') && (
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ color: '#28a745', marginBottom: '15px', borderBottom: '2px solid #28a745', paddingBottom: '5px' }}>
                    Private Rides
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                    {funnelData.overview?.filter(item => item.ride_type === 'private').map((item, index) => (
                      <div 
                        key={index} 
                        className="trip-card" 
                        style={{ 
                          textAlign: 'center',
                          cursor: item.stage === 'private_feedback' ? 'pointer' : 'default',
                          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out'
                        }}
                        onClick={() => item.stage === 'private_feedback' && navigate('/feedback')}
                        onMouseEnter={(e) => item.stage === 'private_feedback' && (e.currentTarget.style.transform = 'translateY(-2px)')}
                        onMouseLeave={(e) => item.stage === 'private_feedback' && (e.currentTarget.style.transform = 'translateY(0px)')}
                      >
                        <FontAwesomeIcon 
                          icon={getFunnelIcon(item.stage)} 
                          style={{ 
                            fontSize: '28px', 
                            color: item.stage === 'private_feedback' ? '#28a745' : '#007bff', 
                            marginBottom: '8px',
                            cursor: item.stage === 'private_feedback' ? 'pointer' : 'default'
                          }} 
                        />
                        <h4 style={{ color: '#2c3e50', margin: '8px 0', fontSize: '14px' }}>{item.label}</h4>
                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#007bff', margin: '8px 0' }}>{item.count}</div>
                        <div style={{ fontSize: '12px', color: '#6c757d' }}>
                          {item.conversion_rate}% conversion
                        </div>
                        {item.stage === 'private_feedback' && (
                          <div style={{ 
                            fontSize: '10px', 
                            color: '#28a745', 
                            marginTop: '8px',
                            fontWeight: 'bold',
                            fontStyle: 'italic'
                          }}>
                            Click to view details
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Rideshare Driver Section */}
              {(selectedRideType === 'all' || selectedRideType === 'rideshare_driver') && (
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ color: '#007bff', marginBottom: '15px', borderBottom: '2px solid #007bff', paddingBottom: '5px' }}>
                    Rideshare Drivers
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                    {funnelData.overview?.filter(item => item.ride_type === 'rideshare_driver').map((item, index) => (
                      <div 
                        key={index} 
                        className="trip-card" 
                        style={{ textAlign: 'center', transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out' }}
                      >
                        <FontAwesomeIcon 
                          icon={getFunnelIcon(item.stage)} 
                          style={{ fontSize: '28px', color: '#007bff', marginBottom: '8px' }} 
                        />
                        <h4 style={{ color: '#2c3e50', margin: '8px 0', fontSize: '14px' }}>{item.label}</h4>
                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#007bff', margin: '8px 0' }}>{item.count}</div>
                        <div style={{ fontSize: '12px', color: '#6c757d' }}>
                          {item.conversion_rate}% conversion
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Rideshare Passenger Section */}
              {(selectedRideType === 'all' || selectedRideType === 'rideshare_passenger') && (
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ color: '#ffc107', marginBottom: '15px', borderBottom: '2px solid #ffc107', paddingBottom: '5px' }}>
                    Rideshare Passengers
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                    {funnelData.overview?.filter(item => item.ride_type === 'rideshare_passenger').map((item, index) => (
                      <div 
                        key={index} 
                        className="trip-card" 
                        style={{ textAlign: 'center', transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out' }}
                      >
                        <FontAwesomeIcon 
                          icon={getFunnelIcon(item.stage)} 
                          style={{ fontSize: '28px', color: '#ffc107', marginBottom: '8px' }} 
                        />
                        <h4 style={{ color: '#2c3e50', margin: '8px 0', fontSize: '14px' }}>{item.label}</h4>
                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#007bff', margin: '8px 0' }}>{item.count}</div>
                        <div style={{ fontSize: '12px', color: '#6c757d' }}>
                          {item.conversion_rate}% conversion
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Detailed Funnel Stages */}
            <div className="trip-cards-container">
              {funnelData.detailed?.filter(ride => 
                selectedRideType === 'all' || ride.ride_type === selectedRideType ||
                (selectedRideType === 'private' && ride.ride_type === 'Private Ride') ||
                (selectedRideType === 'rideshare_driver' && ride.ride_type === 'Rideshare Driver') ||
                (selectedRideType === 'rideshare_passenger' && ride.ride_type === 'Rideshare Passenger')
              ).map((ride, index) => (
                <div key={index} className="trip-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3>Trip ID: {ride.trip_id}</h3>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <span style={{ 
                        padding: '4px 8px', 
                        borderRadius: '12px', 
                        fontSize: '12px',
                        backgroundColor: ride.ride_type === 'Private Ride' ? '#28a74520' : 
                                       ride.ride_type === 'Rideshare Driver' ? '#007bff20' : '#ffc10720',
                        color: ride.ride_type === 'Private Ride' ? '#155724' : 
                               ride.ride_type === 'Rideshare Driver' ? '#004085' : '#856404',
                        fontWeight: 'bold'
                      }}>
                        {ride.ride_type}
                      </span>
                      <span style={{ 
                        padding: '4px 8px', 
                        borderRadius: '12px', 
                        fontSize: '12px',
                        backgroundColor: getStatusColor(ride.status) + '20',
                        color: getStatusColor(ride.status),
                        fontWeight: 'bold'
                      }}>
                        {ride.status}
                      </span>
                    </div>
                  </div>

                  <div className="ride-info-container">
                    <p><strong>Type:</strong> {ride.ride_type}</p>
                    <p><strong>From:</strong> {ride.origin_location || 'N/A'}</p>
                    <p><strong>To:</strong> {ride.dest_location || 'N/A'}</p>
                    <p><strong>Distance:</strong> {ride.distance || 'N/A'}</p>
                    <p><strong>Cost:</strong> ${ride.accepted_cost || ride.delivery_cost_proposed || 'N/A'}</p>
                  </div>

                  {/* Funnel Progress */}
                  <div style={{ margin: '20px 0' }}>
                    <h4 style={{ color: '#2c3e50', marginBottom: '10px' }}>Funnel Progress</h4>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      {(() => {
                        let stages = [];
                        if (ride.ride_type === 'Private Ride') {
                          stages = ['private_search', 'private_new_order', 'private_counter_offer', 'private_matched', 'private_in_progress', 'private_trip_ended', 'private_completed', 'private_waiting_payment', 'private_feedback'];
                        } else if (ride.ride_type === 'Rideshare Driver') {
                          stages = ['rideshare_driver_created', 'rideshare_driver_in_transit', 'rideshare_driver_completed'];
                        } else if (ride.ride_type === 'Rideshare Passenger') {
                          stages = ['rideshare_passenger_request', 'rideshare_passenger_negotiating', 'rideshare_passenger_accepted', 'rideshare_passenger_in_transit', 'rideshare_passenger_completed', 'rideshare_passenger_trip_completed'];
                        }
                        
                        return stages.map((stage, stageIndex) => (
                          <div 
                            key={stage} 
                            style={{ 
                              textAlign: 'center', 
                              flex: 1,
                              cursor: stage === 'private_feedback' && ride.current_stage_index >= stageIndex ? 'pointer' : 'default'
                            }}
                            onClick={() => stage === 'private_feedback' && ride.current_stage_index >= stageIndex && navigate('/feedback')}
                          >
                            <div style={{
                              width: '30px',
                              height: '30px',
                              borderRadius: '50%',
                              backgroundColor: ride.current_stage_index >= stageIndex ? '#28a745' : '#e9ecef',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              margin: '0 auto 5px',
                              cursor: stage === 'private_feedback' && ride.current_stage_index >= stageIndex ? 'pointer' : 'default',
                              transition: 'transform 0.2s ease-in-out'
                            }}
                            onMouseEnter={(e) => stage === 'private_feedback' && ride.current_stage_index >= stageIndex && (e.currentTarget.style.transform = 'scale(1.1)')}
                            onMouseLeave={(e) => stage === 'private_feedback' && ride.current_stage_index >= stageIndex && (e.currentTarget.style.transform = 'scale(1)')}
                            >
                              <FontAwesomeIcon 
                                icon={getFunnelIcon(stage)} 
                                style={{ 
                                  fontSize: '12px', 
                                  color: ride.current_stage_index >= stageIndex ? '#fff' : '#6c757d',
                                  cursor: stage === 'private_feedback' && ride.current_stage_index >= stageIndex ? 'pointer' : 'default'
                                }} 
                              />
                            </div>
                            <div style={{ 
                              fontSize: '10px', 
                              color: '#6c757d',
                              textDecoration: stage === 'private_feedback' && ride.current_stage_index >= stageIndex ? 'underline' : 'none',
                              fontWeight: stage === 'private_feedback' && ride.current_stage_index >= stageIndex ? 'bold' : 'normal'
                            }}>
                              {stage.replace(/^(private_|rideshare_driver_|rideshare_passenger_)/, '').replace('_', ' ')}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                    {ride.ride_type === 'Private Ride' && ride.current_stage_index >= 8 && (
                      <div style={{ 
                        textAlign: 'center', 
                        marginTop: '10px',
                        fontSize: '12px',
                        color: '#28a745',
                        fontStyle: 'italic'
                      }}>
                        Click feedback stage to view details
                      </div>
                    )}
                  </div>

                  {/* Timestamps */}
                  <div style={{ fontSize: '12px', color: '#6c757d', borderTop: '1px solid #e9ecef', paddingTop: '10px' }}>
                    <p><strong>Started:</strong> {ride.request_start_datetime ? new Date(ride.request_start_datetime).toLocaleString() : 'N/A'}</p>
                    {ride.completed_at && <p><strong>Completed:</strong> {new Date(ride.completed_at).toLocaleString()}</p>}
                    {ride.feedback_given_at && <p><strong>Feedback:</strong> {new Date(ride.feedback_given_at).toLocaleString()}</p>}
                  </div>
                </div>
              ))}
            </div>

            {/* Conversion Metrics */}
            {funnelData.metrics && (
              <div style={{ marginTop: '30px' }}>
                <h2 style={{ color: '#2c3e50', marginBottom: '20px' }}>Conversion Metrics</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                  {Object.entries(funnelData.metrics).map(([key, value]) => (
                    <div key={key} className="trip-card" style={{ textAlign: 'center' }}>
                      <h4 style={{ color: '#2c3e50', marginBottom: '10px' }}>{key.replace(/_/g, ' ').toUpperCase()}</h4>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#007bff' }}>{value}%</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Funnel;
